import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Eye, RefreshCw, X, Filter, FileText, GitBranch, Download } from "lucide-react";
import { fmtDateTime, istToday } from "@/lib/dateFormat";
import { useConfig } from "@/hooks/useConfig";
import { useAuth } from "@/contexts/AuthContext";
import { MultiSelectDropdown, SelectedTags } from "@/components/ui/multi-select-dropdown";

const PLATFORM_COLORS = { Blinkit: "bg-blue-100 text-blue-700", Zepto: "bg-purple-100 text-purple-700", "Swiggy Instamart": "bg-orange-100 text-orange-700", Bigbasket: "bg-green-100 text-green-700", Amazon: "bg-yellow-100 text-yellow-800", Unknown: "bg-zinc-100 text-zinc-600" };
const STATUS_COLORS = { Unmatched: "bg-amber-100 text-amber-700", Matched: "bg-green-100 text-green-700", "Manual Review": "bg-purple-100 text-purple-700", Ignored: "bg-zinc-200 text-zinc-600", "Order Created": "bg-green-200 text-green-800" };
const CONF_COLORS = { high: "bg-green-100 text-green-700", medium: "bg-amber-100 text-amber-700", low: "bg-red-100 text-red-700", failed: "bg-zinc-200 text-zinc-600", manual_review: "bg-purple-100 text-purple-700" };

export default function POInboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL params (bookmarkable)
  const [searchText, setSearchText] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [platformFilter, setPlatformFilter] = useState(searchParams.get("platform") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");
  const [hasPdfFilter, setHasPdfFilter] = useState(searchParams.get("has_pdf") === "true");
  const [amendedFilter, setAmendedFilter] = useState(searchParams.get("is_amended") === "true");

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [totalAll, setTotalAll] = useState(0);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [sortCol, setSortCol] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [tradeFormatFilter, setTradeFormatFilter] = useState([]);
  const [platformMapping, setPlatformMapping] = useState([]);

  // Load config for statuses
  const { getValues } = useConfig(["po_inbox_statuses", "channel_types", "po_inbox_platform_mapping"]);
  const PO_STATUSES = getValues("po_inbox_statuses");
  const TRADE_FORMATS = getValues("channel_types");

  // Load distinct platforms and channels on mount
  const [allChannelsPO, setAllChannelsPO] = useState([]);
  useEffect(() => {
    api.get("/po-inbox/platforms").then(r => setPlatforms(r.data || [])).catch(err => console.error("API get /po-inbox/platforms error:", err));
    api.get("/channels").then(r => setAllChannelsPO(r.data || [])).catch(err => console.error("API get /channels error:", err));
  }, []);

  // Build platform → trade format mapping via config + channels
  const poMappingConfig = getValues("po_inbox_platform_mapping");
  const platformToTF = {};
  (poMappingConfig || []).forEach(m => {
    const channelName = m.value;
    const platformName = m.metadata?.platform_name || "";
    const ch = allChannelsPO.find(c => c.name?.toLowerCase() === channelName?.toLowerCase());
    if (ch && platformName) platformToTF[platformName] = ch.type || "";
  });

  // Filter platforms by selected trade formats
  const filteredPlatforms = tradeFormatFilter.length > 0
    ? platforms.filter(p => tradeFormatFilter.includes(platformToTF[p] || ""))
    : platforms;

  // Count active filters
  const activeFilterCount = [statusFilter, platformFilter, dateFrom, dateTo, hasPdfFilter, amendedFilter, ...tradeFormatFilter].filter(Boolean).length;

  // Sync URL params when filters change
  const syncUrlParams = useCallback(() => {
    const params = {};
    if (searchText) params.search = searchText;
    if (statusFilter) params.status = statusFilter;
    if (platformFilter) params.platform = platformFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (hasPdfFilter) params.has_pdf = "true";
    if (amendedFilter) params.is_amended = "true";
    setSearchParams(params, { replace: true });
  }, [searchText, statusFilter, platformFilter, dateFrom, dateTo, hasPdfFilter, amendedFilter, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (statusFilter) params.status = statusFilter;
      if (platformFilter) params.platform = platformFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (hasPdfFilter) params.has_pdf = "true";
      if (amendedFilter) params.is_amended = "true";
      const { data } = await api.get("/po-inbox", { params });
      setItems(data.items || []);
      setSummary(data.summary || {});
      setTotalAll(data.total || 0);
      setTotalFiltered(data.filtered || 0);
    } catch (err) { console.error("API get /po-inbox error:", err); } finally { setLoading(false); }
  }, [searchText, statusFilter, platformFilter, dateFrom, dateTo, hasPdfFilter, amendedFilter]);

  // Debounced search + filter load
  useEffect(() => {
    const t = setTimeout(() => { load(); syncUrlParams(); }, 300);
    return () => clearTimeout(t);
  }, [searchText, statusFilter, platformFilter, dateFrom, dateTo, hasPdfFilter, amendedFilter, load, syncUrlParams]);

  const clearAllFilters = () => {
    setSearchText("");
    setStatusFilter("");
    setPlatformFilter("");
    setDateFrom("");
    setDateTo("");
    setHasPdfFilter(false);
    setAmendedFilter(false);
    setTradeFormatFilter([]);
  };

  const syncGmail = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/gmail/sync");
      toast.success(`Sync complete: ${data.new_emails || 0} new emails`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(false); }
  };

  return (
    <div data-testid="po-inbox-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">PO Inbox</h1>
          <p className="text-sm text-zinc-500 mt-1">Purchase orders received from Qcom platforms via Gmail</p>
        </div>
        <div className="flex gap-2">
          {(user?.roles?.includes("Admin") || user?.roles?.includes("admin") || user?.permissions?.includes("po_inbox.export")) && <Button variant="outline" className="rounded-sm h-8 text-xs" data-testid="export-po-btn" onClick={() => {
            const params = new URLSearchParams();
            if (searchText) params.set("search", searchText);
            if (statusFilter) params.set("status", statusFilter);
            if (platformFilter) params.set("platform", platformFilter);
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            api.get(`/po-inbox/export?${params.toString()}`, { responseType: "blob" }).then(r => {
              const url = window.URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement("a"); a.href = url; a.download = `POInbox_${istToday()}.xlsx`; a.click();
              toast.success("PO Inbox exported");
            }).catch(err => toast.error(err.response?.status === 400 ? "No data to export" : "Export failed"));
          }}><Download size={14} /> Export Excel</Button>}
          <Button onClick={syncGmail} disabled={syncing} variant="outline" className="rounded-sm h-8 text-xs" data-testid="sync-gmail-btn"><RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync Now"}</Button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="flex gap-4 mb-4 text-xs">
        <span className="px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm text-amber-700 font-semibold">{summary.unmatched || 0} unmatched</span>
        <span className="px-2 py-1 bg-green-50 border border-green-200 rounded-sm text-green-700">{summary.matched_today || 0} matched today</span>
      </div>

      {/* Search bar */}
      <div className="relative mb-3" data-testid="po-search-bar">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search PO number, subject, platform..."
          className="pl-9 pr-8 h-9 text-xs rounded-sm"
          data-testid="po-search-input"
        />
        {searchText && (
          <button onClick={() => setSearchText("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" data-testid="po-search-clear">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-end gap-3 mb-3" data-testid="po-filter-row">
        <div>
          <Label className="text-[10px] text-zinc-500 mb-1 block">Trade Format</Label>
          <MultiSelectDropdown label="Trade Format" options={TRADE_FORMATS} selected={tradeFormatFilter} onChange={v => { setTradeFormatFilter(v); if (platformFilter && !filteredPlatforms.includes(platformFilter)) setPlatformFilter(""); }} />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 mb-1 block">Platform</Label>
          <Select value={platformFilter || "all"} onValueChange={v => setPlatformFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8 text-xs rounded-sm" data-testid="po-filter-platform"><SelectValue placeholder="All Platforms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {filteredPlatforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 mb-1 block">Status</Label>
          <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 h-8 text-xs rounded-sm" data-testid="po-filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {PO_STATUSES.length > 0
                ? PO_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)
                : ["Unmatched", "Matched", "Manual Review", "Ignored"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)
              }
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 mb-1 block">From Date</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36 h-8 text-xs rounded-sm" data-testid="po-filter-date-from" />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 mb-1 block">To Date</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36 h-8 text-xs rounded-sm" data-testid="po-filter-date-to" />
        </div>
        <div className="flex items-center gap-1.5 h-8">
          <label className={`flex items-center gap-1.5 px-2 py-1.5 border rounded-sm text-[10px] cursor-pointer transition-colors ${hasPdfFilter ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`} data-testid="po-filter-has-pdf">
            <input type="checkbox" checked={hasPdfFilter} onChange={e => setHasPdfFilter(e.target.checked)} className="w-3 h-3 accent-blue-600" />
            <FileText size={10} /> Has PDF
          </label>
        </div>
        <div className="flex items-center gap-1.5 h-8">
          <label className={`flex items-center gap-1.5 px-2 py-1.5 border rounded-sm text-[10px] cursor-pointer transition-colors ${amendedFilter ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`} data-testid="po-filter-amended">
            <input type="checkbox" checked={amendedFilter} onChange={e => setAmendedFilter(e.target.checked)} className="w-3 h-3 accent-amber-600" />
            <GitBranch size={10} /> Amended
          </label>
        </div>
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 h-8">
            <span className="flex items-center gap-1 text-[10px] text-zinc-500">
              <Filter size={10} /> <span className="font-semibold">{activeFilterCount}</span> filter{activeFilterCount > 1 ? "s" : ""} active
            </span>
            <Button variant="ghost" onClick={clearAllFilters} className="h-6 text-[10px] px-2 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="po-clear-filters">
              <X size={10} /> Clear All
            </Button>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-2" data-testid="po-results-count">
        <p className="text-[10px] text-zinc-500">
          Showing <span className="font-semibold text-zinc-700">{totalFiltered}</span> of <span className="font-semibold text-zinc-700">{totalAll}</span> POs
          {(searchText || activeFilterCount > 0) && totalFiltered !== totalAll && <span className="text-zinc-400 ml-1">(filtered)</span>}
        </p>
      </div>

      <div className="border border-zinc-200 overflow-x-auto" data-testid="po-inbox-table">
        {(() => {
          const toggleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } };
          const SH = ({ col, children }) => (<th className="cursor-pointer select-none hover:bg-zinc-50" onClick={() => toggleSort(col)}>{children} {sortCol === col && <span className="text-[9px]">{sortDir === "asc" ? "\u2191" : "\u2193"}</span>}</th>);
          const sorted = [...items].sort((a, b) => {
            if (!sortCol) return 0;
            const m = { platform: x => (x.platform_name || "").toLowerCase(), po_numbers: x => ((x.extracted_po_numbers || [])[0] || "").toLowerCase(), received: x => x.received_at || "", status: x => (x.status || "").toLowerCase(), created_by: x => (x.order_created_by || "").toLowerCase() };
            const fn = m[sortCol]; if (!fn) return 0;
            const va = fn(a), vb = fn(b);
            return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
          });
          return (
        <table className="data-table w-full">
          <thead><tr><SH col="platform">Platform</SH><SH col="po_numbers">PO Numbers</SH><SH col="received">Received At</SH><th>Subject</th><th>Confidence</th><SH col="status">Status</SH><th>Order</th><SH col="created_by">Created By</SH><th className="w-24">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            sorted.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs">{searchText || activeFilterCount > 0 ? "No POs match your filters." : "No PO emails found. Click \"Sync Now\" to fetch from Gmail."}</td></tr> :
            sorted.map(item => (
              <tr key={item._id} data-testid={`po-row-${item._id}`}>
                <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${PLATFORM_COLORS[item.platform_name] || PLATFORM_COLORS.Unknown}`}>{item.platform_name}</span></td>
                <td className="font-mono text-xs font-semibold">{(item.extracted_po_numbers || []).join(", ") || "\u2014"}{item.amendment_count > 0 && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded-sm">Amended \u00d7{item.amendment_count}</span>}</td>
                <td className="text-[10px] text-zinc-600">{fmtDateTime(item.received_at)}</td>
                <td className="text-xs max-w-[250px] truncate" title={item.subject}>{item.subject}</td>
                <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${CONF_COLORS[item.extraction_confidence] || CONF_COLORS.failed}`}>{item.extraction_confidence}</span></td>
                <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${STATUS_COLORS[item.status] || ''}`}>{item.status}</span></td>
                <td className="text-xs font-mono">{item.matched_order_number ? <button onClick={() => navigate(`/orders/${item.matched_order_id}`)} className="text-blue-600 hover:underline">{item.matched_order_number}</button> : "\u2014"}</td>
                <td className="text-xs text-zinc-500">{item.order_created_by || "\u2014"}</td>
                <td>
                  <div className="flex gap-1 items-center">
                    {item.has_pdf || item.pdf_url ? (
                      <button data-testid={`view-pdf-${item._id}`} onClick={async () => {
                        // Strip /api prefix from pdf_url if present — axios baseURL already adds /api
                        let pdfPath = item.pdf_url || `/po-inbox/${item.gmail_message_id}/pdf`;
                        if (pdfPath.startsWith("/api/")) pdfPath = pdfPath.replace(/^\/api/, "");
                        try {
                          const r = await api.get(pdfPath, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                          window.open(url, '_blank');
                        } catch { toast.error("PDF not available"); }
                      }} className="p-1 hover:bg-blue-50 rounded-sm" title="View PDF"><Eye size={12} className="text-blue-500" /></button>
                    ) : (
                      <span className="text-[9px] text-zinc-400 px-1" data-testid={`no-pdf-${item._id}`}>No PDF</span>
                    )}
                    {item.status === "Unmatched" && <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2" onClick={async () => {
                      const poNum = (item.extracted_po_numbers || [])[0] || "";
                      const poDate = item.po_date || item.received_at?.slice(0, 10) || "";
                      try {
                        const { data: sug } = await api.get(`/po-inbox/${item._id}/suggested-items`);
                        const sugParam = encodeURIComponent(JSON.stringify(sug));
                        navigate(`/orders/new?po_number=${encodeURIComponent(poNum)}&po_date=${encodeURIComponent(poDate)}&suggested=${sugParam}`);
                      } catch {
                        navigate(`/orders/new?po_number=${encodeURIComponent(poNum)}&po_date=${encodeURIComponent(poDate)}`);
                      }
                    }}>Create Order</Button>}
                    {item.status === "Unmatched" && <Button variant="ghost" className="rounded-sm h-6 text-[10px] px-1 text-zinc-500" title="Mark as Ignored" onClick={async () => {
                      const reason = window.prompt("Why are you ignoring this PO?\n\n(Minimum 10 characters \u2014 this reason is visible to all users)");
                      if (!reason || reason.trim().length < 10) { if (reason !== null) toast.error("Reason must be at least 10 characters"); return; }
                      try { await api.put(`/po-inbox/${item._id}/ignore`, { reason: reason.trim() }); toast.success("Marked as ignored"); load(); } catch (err) { toast.error("Failed to ignore"); }
                    }}>Ignore</Button>}
                    {item.status === "Ignored" && (
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-zinc-400 max-w-[120px] truncate" title={`Ignored by ${item.ignored_by_name || '?'}: ${item.ignore_reason || ''}`}>{item.ignore_reason || ''}</span>
                        <Button variant="ghost" className="rounded-sm h-6 text-[10px] px-1 text-blue-600" onClick={async () => {
                          try { await api.put(`/po-inbox/${item._id}/reopen`); toast.success("Reopened"); load(); } catch { toast.error("Failed"); }
                        }}>Reopen</Button>
                      </div>
                    )}
                    {item.status === "Matched" && <Button variant="ghost" className="rounded-sm h-6 text-[10px] px-1" onClick={() => navigate(`/orders/${item.matched_order_id}`)}><Eye size={12} /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          );
        })()}
      </div>
    </div>
  );
}
